import { useStudentComprehensionLevel } from '@/components/assessment';

/**
 * Content personalization utility based on student comprehension level
 */
export class ContentPersonalizer {
  /**
   * Get explanation based on comprehension level
   */
  static getExplanation(concept: string, explanations: Record<number, string>, level: number = 2): string {
    return explanations[level] || explanations[2] || 'Content not available';
  }

  /**
   * Get vocabulary level for concept explanations
   */
  static getVocabularyLevel(level: number): string {
    switch (level) {
      case 1: return 'simple';
      case 2: return 'intermediate';
      case 3: return 'advanced';
      case 4: return 'expert';
      default: return 'intermediate';
    }
  }

  /**
   * Get example complexity based on level
   */
  static getExampleComplexity(level: number): 'basic' | 'intermediate' | 'advanced' | 'expert' {
    switch (level) {
      case 1: return 'basic';
      case 2: return 'intermediate';
      case 3: return 'advanced';
      case 4: return 'expert';
      default: return 'intermediate';
    }
  }

  /**
   * Format mathematical expressions based on comprehension level
   */
  static formatMathExpression(expression: string, level: number): string {
    if (level === 1) {
      // Simplify mathematical notation for basic level
      return expression
        .replace(/∫/g, 'integral of')
        .replace(/∑/g, 'sum of')
        .replace(/∆/g, 'change in')
        .replace(/π/g, 'pi');
    }
    return expression; // Keep original for higher levels
  }
}

/**
 * Hook for personalizing content based on student's comprehension level
 */
export function usePersonalizedContent() {
  const { level, score, hasAssessment, isLoading } = useStudentComprehensionLevel();

  const personalizeExplanation = (explanations: Record<number, string>) => {
    return ContentPersonalizer.getExplanation('', explanations, level);
  };

  const getVocabularyLevel = () => {
    return ContentPersonalizer.getVocabularyLevel(level);
  };

  const getExampleComplexity = () => {
    return ContentPersonalizer.getExampleComplexity(level);
  };

  const formatMathExpression = (expression: string) => {
    return ContentPersonalizer.formatMathExpression(expression, level);
  };

  return {
    level,
    score,
    hasAssessment,
    isLoading,
    personalizeExplanation,
    getVocabularyLevel,
    getExampleComplexity,
    formatMathExpression,
    // Helper flags
    isBasicLevel: level === 1,
    isIntermediateLevel: level === 2,
    isAdvancedLevel: level === 3,
    isExpertLevel: level === 4,
  };
}

/**
 * Example content templates for different comprehension levels
 */
export const SAMPLE_EXPLANATIONS = {
  photosynthesis: {
    1: "Plants make their own food using sunlight. They take in sunlight and water, and make sugar to eat. This is called photosynthesis.",
    2: "Photosynthesis is how plants make food using sunlight. Plants use chlorophyll (the green stuff in leaves) to capture light energy and convert carbon dioxide and water into glucose (sugar) and oxygen.",
    3: "Photosynthesis is a complex biochemical process where plants convert light energy into chemical energy. Chloroplasts contain chlorophyll which absorbs photons, initiating light-dependent reactions that produce ATP and NADPH. These energy carriers fuel the Calvin cycle, where CO₂ is fixed into glucose.",
    4: "Photosynthesis involves two distinct phases: light-dependent reactions occurring in thylakoid membranes generate ATP and NADPH through photophosphorylation, while the Calvin-Benson cycle in the stroma utilizes these energy carriers for carbon fixation via RuBisCO enzyme, ultimately synthesizing glucose through a series of enzymatic reactions involving 3-phosphoglycerate intermediates."
  },
  
  quadraticEquation: {
    1: "A quadratic equation is a math problem with x². To solve it, we can use a special formula that gives us the answer.",
    2: "A quadratic equation has the form ax² + bx + c = 0. We can solve it using the quadratic formula: x = (-b ± √(b²-4ac)) / 2a. This formula works for any quadratic equation.",
    3: "Quadratic equations represent parabolic relationships and can be solved through multiple methods: factoring, completing the square, or the quadratic formula. The discriminant (b²-4ac) determines the nature of solutions - positive yields two real roots, zero gives one repeated root, and negative results in complex conjugate pairs.",
    4: "Quadratic equations arise from second-degree polynomial functions representing conic sections in Cartesian coordinates. Solutions can be derived through algebraic manipulation, graphical interpretation as x-intercepts, or complex analysis when discriminant is negative, yielding conjugate pairs in the complex plane with implications for mathematical modeling of oscillatory systems."
  },

  cellDivision: {
    1: "Cells split into two new cells. This is how living things grow and make new parts of their body.",
    2: "Cell division is the process where one cell divides to create two identical cells. This happens in two main types: mitosis (for growth and repair) and meiosis (for making reproductive cells like sperm and eggs).",
    3: "Cell division involves highly regulated processes: mitosis produces diploid somatic cells through phases (prophase, metaphase, anaphase, telophase) with chromosome condensation and spindle formation, while meiosis creates haploid gametes through two divisions with crossing over and independent assortment ensuring genetic diversity.",
    4: "Cell division mechanisms involve complex molecular machinery including cyclin-dependent kinases regulating checkpoints, chromosome segregation via kinetochore-microtubule dynamics, and cytokinesis through contractile ring formation. Meiotic recombination involves double-strand break repair, synaptonemal complex formation, and resolution of Holliday junctions, ensuring proper chromosome disjunction and genetic recombination."
  }
} as const;

/**
 * Get personalized content for a concept
 */
export function getPersonalizedExplanation(
  concept: keyof typeof SAMPLE_EXPLANATIONS,
  level: number = 2
): string {
  // Ensure level is between 1-4
  const validLevel = Math.min(Math.max(level, 1), 4) as 1 | 2 | 3 | 4;
  
  return SAMPLE_EXPLANATIONS[concept]?.[validLevel] || SAMPLE_EXPLANATIONS[concept]?.[2] || 'Explanation not available';
}
